/**
 * Form Error Handler Utility
 * Handles highlighting and managing field errors from API responses
 */

/**
 * Highlight fields with errors based on fieldIds from the API response
 * @param {Object} errorResponse - The error response containing fieldIds
 * @param {Object} viewRefs - References to field components
 * @param {Function} scrollToField - Function to scroll to a field
 * @returns {Array} - Array of field IDs with errors
 */
export const highlightFieldErrors = (
  errorResponse,
  viewRefs,
  scrollToField
) => {
  // Extract fieldIds from error response
  const errorFieldIds = Array.isArray(errorResponse.fieldIds)
    ? errorResponse.fieldIds
    : [];

  // If we found fieldIds, set errors
  if (errorFieldIds.length > 0) {
    // Set errors for each field
    setErrorsForFields(errorFieldIds, viewRefs);

    // Scroll to the first field with error
    if (scrollToField) {
      scrollToField(errorFieldIds[0]);
    }

    return errorFieldIds;
  }

  return [];
};

/**
 * Set error state for specific fields
 * @param {Array} fieldIds - Array of field IDs with errors
 * @param {Object} viewRefs - References to field components
 */
export const setErrorsForFields = (fieldIds, viewRefs) => {
  // Mark fields with errors
  fieldIds.forEach(fieldId => {
    setFieldError(fieldId, viewRefs);
  });
};

/**
 * Set error for a specific field based on its ID
 * @param {string} fieldId - Field ID to mark as error
 * @param {Object} viewRefs - References to field components
 */
export const setFieldError = (fieldId, viewRefs) => {
  const fieldWrapper = document.querySelector(`[data-field-id="${fieldId}"]`);

  if (fieldWrapper) {
    // Add a visual error indicator class
    fieldWrapper.classList.add("field-error");

    // Try to find the component instance in refs
    const fieldRef = findFieldComponentRef(fieldId, viewRefs);
    if (fieldRef && typeof fieldRef.setExternalError === "function") {
      // Use the component's method to set the error
      fieldRef.setExternalError("This is a required question");
    } else {
      // Fallback to DOM manipulation
      const errorContainer = fieldWrapper.querySelector(
        '[data-section-error="true"]'
      );
      if (errorContainer) {
        errorContainer.textContent = "This is a required question";
        errorContainer.style.display = "block";
      }
    }
  }
};

/**
 * Clear all field errors
 * @param {Array} errorFieldIds - Array of field IDs with errors
 * @param {Object} viewRefs - References to field components
 */
export const clearFieldErrors = (errorFieldIds, viewRefs) => {
  if (errorFieldIds && errorFieldIds.length) {
    errorFieldIds.forEach(fieldId => {
      const fieldWrapper = document.querySelector(
        `[data-field-id="${fieldId}"]`
      );
      if (fieldWrapper) {
        fieldWrapper.classList.remove("field-error");

        // Try to find the component instance and use its method
        const fieldRef = findFieldComponentRef(fieldId, viewRefs);
        if (fieldRef && typeof fieldRef.clearErrors === "function") {
          // Use the component's method to clear errors
          fieldRef.clearErrors();
        } else {
          // Fallback to DOM manipulation
          const errorContainer = fieldWrapper.querySelector(
            '[data-section-error="true"]'
          );
          if (errorContainer) {
            errorContainer.textContent = "";
            errorContainer.style.display = "none";
          }
        }
      }
    });
  }
};

/**
 * Find field component reference by ID
 * @param {string} fieldId - Field ID to find component for
 * @param {Object} viewRefs - References to field components
 * @returns {Object|null} - Component reference or null
 */
export const findFieldComponentRef = (fieldId, viewRefs) => {
  // Check if we have any field components registered
  for (const refKey in viewRefs) {
    const ref = viewRefs[refKey];

    // If this is a form component with the matching field ID
    if (ref && ref.props && ref.props.field && ref.props.field.id === fieldId) {
      return ref;
    }

    // If this is a wrapped component, try to access the instance
    if (ref && ref.wrappedInstance) {
      const instance = ref.wrappedInstance;
      if (
        instance.props &&
        instance.props.field &&
        instance.props.field.id === fieldId
      ) {
        return instance;
      }
    }
  }

  return null;
};
